import { EMPLOYEE_ROLE } from 'constants/auth'
import config from 'constants/config'
import { isRecord } from 'lib/object/objectUtils'
import {
  get as getLocalStorage,
  remove as removeLocalStorage,
  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { isLEStaff } from 'selectors/accountSelectors'

const STORAGE_KEY_PREFIX = 'ExperimentControlPanel:employeeAccess'
const STORAGE_VERSION = 1
const ACCESS_DURATION_MS = 12 * 60 * 60 * 1000

interface PersistedExperimentPanelAccess {
  readonly version: number
  readonly expiresAt: number
}

function getStorageKey(): string {
  return `${STORAGE_KEY_PREFIX}:${config.BRAND}`
}

function getPersistedAccess(): PersistedExperimentPanelAccess | undefined {
  const value = getLocalStorage(getStorageKey())
  if (!isRecord(value) || value.version !== STORAGE_VERSION ||
    typeof value.expiresAt !== 'number' ||
    value.expiresAt <= Date.now()) {
    return undefined
  }

  return {
    version: STORAGE_VERSION,
    expiresAt: value.expiresAt,
  }
}

/**
 * Records that this browser recently received the employee role from the auth
 * service. The browser origin isolates staging and production storage.
 */
export function rememberExperimentPanelEmployeeAccess(account: App.AuthAccount): void {
  if (!account.memberId || !account.roles.includes(EMPLOYEE_ROLE)) {
    removeLocalStorage(getStorageKey())
    return
  }

  setLocalStorage(getStorageKey(), {
    version: STORAGE_VERSION,
    expiresAt: Date.now() + ACCESS_DURATION_MS,
  })
}

export function hasRememberedExperimentPanelAccess(): boolean {
  return !!getPersistedAccess()
}

export function canAccessExperimentPanel(state: App.State): boolean {
  return isLEStaff(state) || hasRememberedExperimentPanelAccess()
}
